import { useCallback, useEffect, useState } from "react";
import { customersApi, type LookupList } from "../api/customersApi";

export type LookupOption = { key: string; labelHe: string };

/**
 * Loads tenant lookup options for one or more list keys via GET /lookups.
 */
export function useLookupOptions(listKeys: string | string[]): {
  optionsByList: Record<string, LookupOption[]>;
  options: LookupOption[];
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const keys = Array.isArray(listKeys) ? listKeys : [listKeys];
  const keysKey = keys.join(",");
  const [optionsByList, setOptionsByList] = useState<Record<string, LookupOption[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (keys.length === 0) {
      setOptionsByList({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all(
      keys.map(async (listKey) => {
        const lists = await customersApi.listLookups(listKey).catch(() => [] as LookupList[]);
        return [listKey, lists[0]?.options ?? []] as const;
      }),
    )
      .then((entries) => {
        if (cancelled) return;
        setOptionsByList(Object.fromEntries(entries));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "שגיאה בטעינת רשימות");
        setOptionsByList({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keysKey captures keys
  }, [keysKey, tick]);

  const primaryKey = keys[0] ?? "";
  const options = optionsByList[primaryKey] ?? [];

  return { optionsByList, options, loading, error, reload };
}

export function lookupLabelMap(
  options: LookupOption[],
): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.key, o.labelHe]));
}
